import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { RegisterOrganizationFields } from 'src/app/RegisterOrganization/RegisterOrganization'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import TextDropdown, {
  TextOption,
} from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface RegisterOrganizationFormProps {
  flatFields: RegisterOrganizationFields
  onFlatFieldChange: (
    fieldName: keyof RegisterOrganizationFields,
    value: RegisterOrganizationFields[keyof RegisterOrganizationFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<keyof RegisterOrganizationFields, Array<React.ReactElement | string>>
  >
  industryOptions: TextOption[]
  onSubmit: () => void
  topLevelError?: string
}

const RegisterOrganizationForm: React.FC<RegisterOrganizationFormProps> = ({
  flatFields,
  flatFieldsErrors,
  onFlatFieldChange,
  industryOptions,
  onSubmit,
  topLevelError,
}): React.ReactElement => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    onSubmit()
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          {translate(
            'Please let us know the ${legalNameBold} (i.e., not a subdivision), and ${industryBold}.',
            {
              legalNameBold: (
                <b>{translate('legal name of your organization')}</b>
              ),
              industryBold: <b>{translate("your org's industry")}</b>,
            },
          )}
        </span>
      </div>
      <form
        onSubmit={handleOnSubmit}
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <Fragment>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              margin-top: 40px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  20,
                  styleConstants.colors.black,
                  styleConstants.fontFamilies.medium,
                )}
              `}
            >
              {translate('Organization info')}
            </span>
            <TextInputField
              errors={
                topLevelError !== undefined
                  ? [topLevelError]
                  : flatFieldsErrors['name']
              }
              design={TextInputFieldDesigns.New}
              fieldLabel={translate('Organization Name')}
              value={flatFields['name'] || ''}
              onChange={(val: string): void => {
                onFlatFieldChange('name', val)
              }}
              placeholder={translateSingleString(locale, 'Enter your org name')}
              css={css`
                margin-top: 20px;
              `}
            />
            <TextDropdown
              fieldLabel={translate('Industry')}
              optionGroups={[
                {
                  label: translate('Existing Options'),
                  options: industryOptions,
                },
              ]}
              isMulti={false}
              selectedValues={
                flatFields['industry'] !== undefined
                  ? new Set([flatFields['industry']])
                  : new Set<string>()
              }
              chooseOption={(val: string): void => {
                onFlatFieldChange('industry', val)
              }}
              placeholder={translateSingleString(
                locale,
                'Select or type in your industry',
              )}
              errors={flatFieldsErrors['industry']}
              validateNewOption={(val: string): boolean => val.length > 0}
              newOptionLabel={translate('New Label')}
              isCreatable
              css={css`
                margin-top: 20px;
              `}
            />
          </div>
        </Fragment>
        <Button
          type={ButtonTypes.submit}
          design={ButtonDesigns.designCta}
          text={translate('Confirm Organization')}
          big
          css={css`
            align-self: flex-start;
            margin-top: 40px;
          `}
        />
      </form>
    </div>
  )
}

export default RegisterOrganizationForm
